// Welt-Uhr des Clients: WebSocket-Verbindung zu /ws, Schätzung der Serverzeit und
// Wiederverbindung mit wachsender Wartezeit. Die Serverzeit ist Unix-Zeit in Millisekunden;
// der Client hält sie zwischen zwei Nachrichten als `now() + offset` selbst weiter und gleicht
// `offset` bei jeder clock-Nachricht sanft an (docs/architektur.md „Protokoll und Uhr“,
// ADR 0004). Die reine Schätzlogik liegt in `ClockEstimator` und ist ohne Netz getestet.

import {
  clockOffsetUpdate,
  encodeMessage,
  parseServerMessage,
  type ClientMessage,
  type ClockMessage,
  type HelloMessage,
  type ServerMessage,
} from '@bauernhof/shared';

/** Anteil, um den sich die Offset-Schätzung je clock-Nachricht der Messung nähert. */
export const CLOCK_SMOOTHING_ALPHA = 0.2;

/** Wartezeit vor dem ersten neuen Versuch und die Obergrenze; dazwischen verdoppelt sie sich. */
export const RECONNECT_DELAYS_MS = { first: 1_000, max: 10_000 } as const;

/**
 * Schätzt die Serverzeit aus den Nachrichten des Servers: `hello` setzt den Offset hart (auch
 * nach einer Wiederverbindung), `clock` glättet ihn. Ohne Nachricht ist der Offset unbekannt und
 * `serverNow()` liefert die lokale Zeit als Notbehelf, damit das Licht nie undefiniert ist.
 */
export class ClockEstimator {
  private offsetMs: number | null = null;
  private world: string | null = null;

  constructor(
    private readonly now: () => number,
    private readonly alpha: number = CLOCK_SMOOTHING_ALPHA,
  ) {}

  /**
   * Verarbeitet eine Nachricht, die eine Serverzeit trägt; die Messung ist `serverTime - now()`
   * beim Empfang. Die übrigen Nachrichten des Protokolls gehen die Uhr nichts an.
   */
  apply(msg: HelloMessage | ClockMessage): void {
    const messung = msg.serverTime - this.now();
    if (msg.t === 'hello') {
      this.offsetMs = messung;
      this.world = msg.worldId;
      return;
    }
    this.offsetMs = clockOffsetUpdate(this.offsetMs, messung, this.alpha);
  }

  /** Geschätzte Serverzeit in Unix-Millisekunden; vor der ersten Nachricht die lokale Zeit. */
  serverNow(): number {
    return this.now() + (this.offsetMs ?? 0);
  }

  /** Aktuelle Offset-Schätzung oder null, solange keine Nachricht angekommen ist. */
  offset(): number | null {
    return this.offsetMs;
  }

  /** Ob mindestens eine Uhr-Nachricht verarbeitet wurde. */
  synced(): boolean {
    return this.offsetMs !== null;
  }

  /** Weltkennung aus der letzten Begrüßung oder null davor. */
  worldId(): string | null {
    return this.world;
  }
}

/** Wartezeit in Millisekunden vor dem Versuch mit der Nummer `attempt` (0 = erster neuer Versuch). */
export function reconnectDelayMs(attempt: number): number {
  const stufe = Number.isFinite(attempt) ? Math.max(0, Math.floor(attempt)) : 0;
  // 2^stufe wächst schnell über die Obergrenze hinaus; ab dort zählt nur noch das Minimum.
  const wartezeit = RECONNECT_DELAYS_MS.first * 2 ** Math.min(stufe, 30);
  return Math.min(wartezeit, RECONNECT_DELAYS_MS.max);
}

/** Adresse der Welt-Uhr passend zur Seite: ws bei http, wss bei https, Pfad /ws. */
export function worldClockUrl(location: { protocol: string; host: string }): string {
  const schema = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${schema}//${location.host}/ws`;
}

/** Der Ausschnitt eines WebSockets, den die Uhr braucht; ein echter `WebSocket` erfüllt ihn. */
export interface ClockSocketLike {
  addEventListener(type: 'open', listener: () => void): void;
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  addEventListener(type: 'close', listener: () => void): void;
  addEventListener(type: 'error', listener: () => void): void;
  send(data: string): void;
  close(): void;
}

export type ClockSocketConstructor = new (url: string) => ClockSocketLike;

export interface WorldClockDeps {
  /** Lokale Zeitquelle in Unix-Millisekunden; Standard Date.now. */
  now?: () => number;
  /** Socket-Klasse; Standard der `WebSocket` des Browsers. Tests geben eine Attrappe. */
  WebSocketCtor?: ClockSocketConstructor;
  /**
   * Jede gültige Nachricht des Servers, auch die, die die Uhr nichts angehen. main.ts verteilt sie
   * mit `verarbeiteNachricht` an die Handler des Spiels.
   */
  onMessage?: (msg: ServerMessage) => void;
  /**
   * Jeder Verbindungsabbruch, vor der geplanten Wiederverbindung. Der Client fragt daraufhin
   * `GET /api/ich`: Wurde die Sitzung woanders fortgesetzt, weist der Server den Handshake mit 401
   * ab, und das ließe sich am Socket allein nicht erkennen (der Browser verrät den Status nicht).
   */
  onGetrennt?: () => void;
}

/**
 * Lebenslage der Verbindung: `connecting` bis zum ersten geglückten Aufbau (auch über mehrere
 * Versuche), `connected` bei offener Verbindung, `reconnecting` nach einem Abbruch, `closed`
 * nach `dispose`. Das HUD zeigt „getrennt“ nur bei `reconnecting`, nicht schon beim Start.
 */
export type WorldClockStatus = 'connecting' | 'connected' | 'reconnecting' | 'closed';

export interface WorldClock {
  /** Geschätzte Serverzeit in Unix-Millisekunden; vor der ersten Nachricht die lokale Zeit. */
  serverNow(): number;
  /** Ob die Verbindung gerade offen ist. */
  connected(): boolean;
  /** Feiner als `connected`: unterscheidet den ersten Aufbau von einer Wiederverbindung. */
  status(): WorldClockStatus;
  /** Weltkennung aus der Begrüßung des Servers oder null, solange keine kam. */
  worldId(): string | null;
  /**
   * Schickt eine Nachricht des Clients (seit task-013: `move`, `interact`). Nur über eine offene
   * Verbindung; ohne sie geht nichts hinaus und nichts wird für später aufgehoben – eine alte
   * Position oder ein alter Tastendruck wäre nach der Wiederverbindung falsch. Liefert, ob die
   * Nachricht hinausging.
   */
  send(msg: ClientMessage): boolean;
  /** Schließt die Verbindung, bricht eine geplante Wiederverbindung ab und nimmt nichts mehr an. */
  dispose(): void;
}

/**
 * Verbindet sich mit der Welt-Uhr unter `url` und hält die Verbindung: Nach einem Abbruch wird
 * mit 1 s, 2 s, 4 s, 8 s und dann alle 10 s neu verbunden; nach einer geglückten Begrüßung
 * beginnt die Reihe von vorn. Ungültige Nachrichten (falsche Version, unbekannter Typ, kein
 * Text) werden still verworfen; die Prüfung macht `parseServerMessage` aus shared.
 */
export function createWorldClock(url: string, deps: WorldClockDeps = {}): WorldClock {
  const now = deps.now ?? ((): number => Date.now());
  const Socket: ClockSocketConstructor = deps.WebSocketCtor ?? WebSocket;
  const schaetzer = new ClockEstimator(now);

  let socket: ClockSocketLike | null = null;
  let verbunden = false;
  let jemalsVerbunden = false;
  let fehlversuche = 0;
  let wartezeit: ReturnType<typeof setTimeout> | null = null;
  let beendet = false;

  const planeNeuverbindung = (): void => {
    if (beendet || wartezeit !== null) return;
    const dauer = reconnectDelayMs(fehlversuche);
    fehlversuche++;
    wartezeit = setTimeout(() => {
      wartezeit = null;
      verbinde();
    }, dauer);
  };

  const verbinde = (): void => {
    if (beendet) return;
    let neu: ClockSocketLike;
    try {
      neu = new Socket(url);
    } catch {
      // Etwa eine unbrauchbare Adresse oder ein Browser ohne WebSocket-Erlaubnis: später noch einmal.
      planeNeuverbindung();
      return;
    }
    socket = neu;
    // Ereignisse eines abgelösten Sockets (nach dispose oder Wiederverbindung) zählen nicht mehr.
    const istAktuell = (): boolean => socket === neu;

    neu.addEventListener('open', () => {
      if (!istAktuell()) return;
      verbunden = true;
      jemalsVerbunden = true;
    });
    neu.addEventListener('message', (event) => {
      if (!istAktuell() || typeof event.data !== 'string') return;
      const msg = parseServerMessage(event.data);
      if (msg === null) return;
      if (msg.t === 'hello') fehlversuche = 0;
      if (msg.t === 'hello' || msg.t === 'clock') schaetzer.apply(msg);
      deps.onMessage?.(msg);
    });
    neu.addEventListener('error', () => {
      // Der Browser meldet nach einem Fehler immer auch close; das Schließen ist nur Vorsicht.
      if (istAktuell()) neu.close();
    });
    neu.addEventListener('close', () => {
      if (!istAktuell()) return;
      socket = null;
      verbunden = false;
      deps.onGetrennt?.();
      planeNeuverbindung();
    });
  };

  verbinde();

  const status = (): WorldClockStatus => {
    if (beendet) return 'closed';
    if (verbunden) return 'connected';
    return jemalsVerbunden ? 'reconnecting' : 'connecting';
  };

  return {
    serverNow: () => schaetzer.serverNow(),
    connected: () => verbunden,
    status,
    worldId: () => schaetzer.worldId(),
    send: (msg) => {
      if (beendet || !verbunden || socket === null) return false;
      try {
        socket.send(encodeMessage(msg));
        return true;
      } catch {
        // Der Browser wirft, wenn der Socket gerade schließt; das close-Ereignis folgt ohnehin.
        return false;
      }
    },
    dispose: () => {
      beendet = true;
      if (wartezeit !== null) {
        clearTimeout(wartezeit);
        wartezeit = null;
      }
      const alt = socket;
      socket = null;
      verbunden = false;
      alt?.close();
    },
  };
}
